<template>
  <reflink-banner-box
    v-if="refLink != null"
    class="reflink-banner ms-3 me-3 mb-6"
    :reflink="refLink"
    @dismissed="hideNotification(refLink.reflink)"
  />
</template>

<script lang="ts" setup>
import {
  GetReflinkInformationRequest,
  TrackReflinkRequest,
} from 'gportal-grpc/gportal/api/marketing/v1/requests_pb';
import { Reflink } from 'gportal-grpc/gportal/api/marketing/v1/reflink_pb';
import {
  GetActiveEventDocument,
  type GetActiveEventQuery,
} from '~/apollo/types/types';
import { Code } from '@connectrpc/connect';

const { $sentry } = useNuxtApp();
const { client: $grpcMarketing } = useGrpcMarketing();
const refLink = ref<Reflink>();

// Cookie for referrer
const refCookieName = 'CakeCookie[ref]';
const refCookieExpire = new Date();
refCookieExpire.setMonth(refCookieExpire.getMonth() + 1);
const refCookie = useCookie(refCookieName, {
  expires: refCookieExpire,
  secure: true,
  httpOnly: false,
  path: '/',
});

const isNotificationHidden = (referrer: string) => {
  const notificationsJson = localStorage.getItem('hidden-notifications');
  if (notificationsJson) {
    const hiddenNotifications = JSON.parse(notificationsJson) as Array<string>;
    if (hiddenNotifications.includes(`reflink-${referrer}`)) {
      return true;
    }
  }

  return false;
};

// TODO: Refactor to Notification Component
const hideNotification = (referrer: string) => {
  const id = `reflink-${referrer}`;
  const e = document.querySelector(`div[data-notification-id="${id}"]`);
  e.parentNode.removeChild(e);

  const notificationsJson = localStorage.getItem('hidden-notifications');
  let hiddenNotifications = [];
  if (notificationsJson) {
    hiddenNotifications = JSON.parse(notificationsJson);
  }
  hiddenNotifications.push(id);

  window.localStorage.setItem(
    'hidden-notifications',
    JSON.stringify(hiddenNotifications),
  );
};

const loadEventReflink = async (): Promise<string | null> => {
  try {
    const { data: response }: { data: GetActiveEventQuery } =
      await useLegacyApollo<GetActiveEventQuery, GetActiveEventQuery>(
        GetActiveEventDocument,
      );

    if (response.event != null && response.event.reflink != null) {
      return response.event.reflink;
    }
  } catch (error) {
    $sentry.captureException(error);
  }

  return null;
};

const fetchRefLink = async (referer: string): Promise<Reflink> => {
  const { response, error } = await $grpcMarketing.executeApiCallFallback(
    'getReflinkInformation',
    new GetReflinkInformationRequest({
      refId: referer,
    }),
  );

  if (error != null) {
    if (error.code != Code.NotFound) {
      console.warn('Error while fetching RefLink', error.message);
      $sentry.captureException(error);
    }
    return null;
  }

  return response.reflink;
};

const trackRefLink = async (referrer: string) => {
  const { response, error } = await $grpcMarketing.executeApiCallFallback(
    'trackReflink',
    new TrackReflinkRequest({
      refId: referrer,
    }),
  );

  if (error != null) {
    $sentry.captureException(error);
    return null;
  }

  return response;
};

const loadReflink = async (referer: string) => {
  if (isNotificationHidden(referer)) {
    return;
  }

  const resp = await fetchRefLink(referer);
  if (resp != null) {
    refLink.value = resp;
  }
};

const route = useRoute();

const refQuery = computed(() => {
  if (!route.query.ref) {
    return null;
  }

  if (typeof route.query.ref === 'string') {
    return route.query.ref;
  }

  return route.query.ref[0];
});

onMounted(async () => {
  // Get reflink from cookie
  const activeReflink: string = refQuery.value || refCookie.value;

  // If no cookie reflink, check for strapi reflink
  if (activeReflink == null || activeReflink == '') {
    await loadEventReflink();
  }

  if (activeReflink == null || activeReflink == '') {
    return;
  }

  const oldCookieValue = refCookie.value;
  if (activeReflink != refCookie.value) {
    refCookie.value = activeReflink;
  }

  await loadReflink(activeReflink);
  if (refLink.value != null && activeReflink != oldCookieValue) {
    await trackRefLink(activeReflink);
  }
});
</script>

<style lang="scss" scoped>
@import 'bootstrap/scss/mixins/breakpoints';
@import 'gportal-theme/scss/breakpoints.scss';
@import 'gportal-theme/scss/colors';

.reflink-banner {
  position: fixed;
  top: 1rem;
  z-index: 101;
  right: 0;

  @include media-breakpoint-up(lg) {
    top: 1rem;
    right: 0;
  }
}
</style>
